import { Formik } from 'formik';
import _ from 'lodash';
import React, { useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom/cjs/react-router-dom.min';
import * as Yup from 'yup';
import {
  Button,
  Buttons,
  CancelButton,
  DeleteButton,
  Field,
  Form,
  FormMessage,
  HelpTooltip,
  Level,
  ModalCard,
  Radio,
} from '~/components';
import { useApi, useMember, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm } from '~/hooks';
import { emptyStringToNull, mergeValues } from '~/utils';
import { useReports } from './ReportsContext';

export default function ReportForm({ title, report, search, onClose, onSaved, onDelete }) {
  const reportsContext = useReports();
  const api = useApi();
  const { workspace } = useWorkspace();
  const {
    member: { securityRole },
  } = useMember();

  const [{ status, message, isSubmitting }, form] = useForm();
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const toast = useToast();

  const { area } = useParams();

  const history = useHistory();
  const location = useLocation();

  async function handleSubmit(values) {
    try {
      form.submit();
      const params = new URLSearchParams(search ?? location.search);
      params.set('dates', values.dates);

      if (report.id) {
        const body = emptyStringToNull({
          name: values.name,
          description: values.description,
          params: _.toArray(params.entries()),
        });
        await api.www.workspaces(workspace.id).reports(report.id).update(body);
        await reportsContext.fetchData();
      } else {
        const body = emptyStringToNull({
          scopeKey: values.scopeKey,
          name: values.name,
          description: values.description,
          templateKey: report.templateKey,
          generatorKey: report.generatorKey,
          params: _.toArray(params.entries()),
        });
        const { data } = await api.www.workspaces(workspace.id).reports().add(body);
        await reportsContext.fetchData();
        history.replace(`/app/${workspace.key}/reports/${area}/${data.slug}`);
      }

      await onSaved();

      toast.success(report.id ? 'Report updated successfully.' : 'Report saved successfully.');

      form.done();
    } catch ({ message }) {
      form.error({ message });
    }
  }

  const initialValues = mergeValues(
    {
      name: '',
      description: '',
      dates: 'fixed',
      scopeKey: 'member',
    },
    {
      ...report,
      dates:
        _.find(report.params, ([key, value]) => key === 'dates' && ['fixed', 'relative'].includes(value))?.[1] ??
        'fixed',
    },
  );

  const handleClose = () => dirtyCheck(onClose);

  return (
    <>
      <ModalCard title={title} onClose={handleClose}>
        <Formik
          innerRef={formRef}
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={Yup.object().shape({
            name: Yup.string().label('Name').max(255).required(),
            description: Yup.string().label('Description').max(5000),
          })}>
          {(formik) => {
            return (
              <Form>
                <ModalCard.Body>
                  <>
                    <Form.Control>
                      <Field.Text autoFocus name="name" placeholder="Name" maxLength={255} />
                    </Form.Control>

                    <Form.Control>
                      <Field.Text name="description" placeholder="Description" maxLength={5000} />
                    </Form.Control>

                    <Form.Control>
                      <Field.RadioGroup name="dates">
                        <Radio
                          value="fixed"
                          label={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              Use fixed dates
                              <HelpTooltip
                                style={{ marginLeft: '0.5rem' }}
                                message="The report will always use the specified dates."
                              />
                            </div>
                          }
                        />
                        <Radio
                          value="relative"
                          label={
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              Use relative dates
                              <HelpTooltip
                                style={{ marginLeft: '0.5rem' }}
                                message={`When the report is run, the relative date filter (such as "Last Year" or "This Month") will be resolved to determine the report dates.`}
                              />
                            </div>
                          }
                        />
                      </Field.RadioGroup>
                    </Form.Control>

                    {!report.id && securityRole.manageWorkspace && (
                      <Form.Control>
                        <Field.RadioGroup name="scopeKey">
                          <Radio value="member" label="Save to My Reports" />
                          <Radio value="workspace" label="Save to the Library" />
                        </Field.RadioGroup>
                      </Form.Control>
                    )}
                  </>
                  {status && <FormMessage.Error>{message || 'An error has occurred.'}</FormMessage.Error>}
                </ModalCard.Body>

                <ModalCard.Footer>
                  <Level>
                    {onDelete && <DeleteButton onClick={onDelete}>Delete</DeleteButton>}

                    <Buttons align="right">
                      <CancelButton onClick={handleClose}>Close</CancelButton>
                      <Button onClick={formik.submitForm} isLoading={isSubmitting}>
                        Save
                      </Button>
                    </Buttons>
                  </Level>
                </ModalCard.Footer>
              </Form>
            );
          }}
        </Formik>
      </ModalCard>
    </>
  );
}
